import { useContext, useContextSelector } from 'use-context-selector';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { AxiosError } from 'axios';
import AuthorizationContext from './AuthorizationContext';

const useAuthorizationState = () => {
  const context = useContext(AuthorizationContext);

  if (context === undefined) {
    throw new Error('useAuthorizationState must be used within AuthorizationProvider');
  }

  return context;
};

const useAuthorizationSelectedAuthorization = () => {
  const context = useContextSelector(AuthorizationContext, state => state.selectedAuthorization);

  if (context === undefined) {
    throw new Error('useAuthorizationSelectedAuthorization must be used within AuthorizationProvider');
  }

  return context;
};

const useAuthorizationTransactionDetails = () => {
  const context = useContextSelector(AuthorizationContext, state => state.transactionDetails);

  if (context === undefined) {
    throw new Error('useAuthorizationTransactionDetails must be used within AuthorizationProvider');
  }

  return context;
};

const useAuthorizationSelectAction = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onSelectAuthorization);

  if (context === undefined) {
    throw new Error('useAuthorizationSelectAction must be used within AuthorizationProvider');
  }

  return context;
};

const useAuthorizationLoadingState = (
  loadingProperty: 'isLoadingTransactionDetails' | 'isLoadingCheckAuthorization',
) => {
  const context = useContextSelector(AuthorizationContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useFavouriteTransactionLoadingState  must be used within AuthorizationProvider');
  }

  return context[loadingProperty];
};

const useAuthorizationErrorState = (
  errorProperty: 'transactionDetailsError' | 'checkAuthorizationError',
): AxiosError<APIGatewayResponse<unknown>, any> | undefined => {
  const context = useContextSelector(AuthorizationContext, state => state.errorState);

  if (context === undefined) {
    throw new Error('useAuthorizationErrorState must be used within AuthorizationProvider');
  }

  return context[errorProperty];
};

const useIsAuthorizationTransactionDetailsDrawerOpen = () => {
  const context = useContextSelector(AuthorizationContext, state => state.isAuthorizationTransactionDetailsDrawerOpen);

  if (context === undefined) {
    throw new Error('useIsAuthorizationTransactionDetailsDrawerOpen must be used within AuthorizationProvider');
  }

  return context;
};

const useToggleAuthorizationTransactionDetailsDrawer = () => {
  const context = useContextSelector(AuthorizationContext, state => state.toggleAuthorizationTransactionDetailsDrawer);

  if (context === undefined) {
    throw new Error('useToggleAuthorizationTransactionDetailsDrawer must be used within AuthorizationProvider');
  }

  return context;
};

const useOnToggleAuthFlowDrawer = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onToggleAuthFlowDrawer);

  if (context === undefined) {
    throw new Error('useOnToggleAuthFlowDrawer must be used within AuthorizationProvider');
  }

  return context;
};

const useIsAuthFlowDrawerOpen = () => {
  const context = useContextSelector(AuthorizationContext, state => state.isAuthFlowDrawerOpen);

  if (context === undefined) {
    throw new Error('useIsAuthFlowDrawerOpen must be used within AuthorizationProvider');
  }

  return context;
};

const useCheckAuthorization = () => useContextSelector(AuthorizationContext, state => state.checkAuthorizationData);

const useOnTransactionDetailsMutation = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onTransactionDetailsMutation);

  if (context === undefined) {
    throw new Error('useOnTransactionDetailsMutation must be used within AuthorizationProvider');
  }

  return context;
};

const useExecuteMandateService = () => {
  const context = useContextSelector(AuthorizationContext, state => state.executeMandateService);

  if (context === undefined) {
    throw new Error('useExecuteMandateService must be used within AuthorizationProvider');
  }

  return context;
};

const useExecuteBulkMandatesService = () => {
  const context = useContextSelector(AuthorizationContext, state => state.executeBulkMandatesService);

  if (context === undefined) {
    throw new Error('useExecuteBulkMandatesService must be used within AuthorizationProvider');
  }

  return context;
};

const useInitiateMandateService = () => {
  const context = useContextSelector(AuthorizationContext, state => state.initiateMandateService);

  if (context === undefined) {
    throw new Error('useInitiateMandateService must be used within AuthorizationProvider');
  }

  return context;
};

const useInitiateBulkMandatesService = () => {
  const context = useContextSelector(AuthorizationContext, state => state.initiateBulkMandatesService);

  if (context === undefined) {
    throw new Error('useInitiateBulkMandatesService must be used within AuthorizationProvider');
  }

  return context;
};

const useOnCheckAuthorizationMutation = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onCheckAuthorizationMutation);

  if (context === undefined) {
    throw new Error('useOnCheckAuthorizationMutation must be used within AuthorizationProvider');
  }

  return context;
};

const useOnBulkAuthorizationSelect = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onBulkAuthorizationSelect);

  if (context === undefined) {
    throw new Error('useOnBulkAuthorizationSelect must be used within AuthorizationProvider');
  }

  return context;
};

const useBulkSelectedAuthorizations = () => {
  const context = useContextSelector(AuthorizationContext, state => state.bulkAuthorizations ?? []);

  if (context === undefined) {
    throw new Error('useBulkSelectedAuthorizations must be used within AuthorizationProvider');
  }

  return context;
};

const useOnMassiveAuthorizationSelection = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onMassiveAuthorizationSelection);

  if (context === undefined) {
    throw new Error('useOnMassiveAuthorizationSelection must be used within AuthorizationProvider');
  }

  return context;
};

const useOnSetRequestNewRate = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onSetRequestNewRate);

  if (context === undefined) {
    throw new Error('useOnRequestNewRate must be used within AuthorizationProvider');
  }

  return context;
};

const useRequestNewRateFlag = () => {
  const context = useContextSelector(AuthorizationContext, state => state.requestSpecialRate);

  if (context === undefined) {
    throw new Error('useRequestNewRateFlag must be used within AuthorizationProvider');
  }

  return context;
};

const useAuthAction = () => {
  const context = useContextSelector(AuthorizationContext, state => state.authAction);

  if (context === undefined) {
    throw new Error('useAuthAction must be used within AuthorizationProvider');
  }

  return context;
};

const useOnSetAuthAction = () => {
  const context = useContextSelector(AuthorizationContext, state => state.onSetAuthAction);

  if (context === undefined) {
    throw new Error('onSetAuthAction must be used within AuthorizationProvider');
  }

  return context;
};

const useOnShouldShowRequestNewRateBtn = () => {
  const context = useContextSelector(AuthorizationContext, state => state.shouldShowRequestNewRateBtn);

  if (context === undefined) {
    throw new Error('useOnShouldShowRequestNewRateBtn must be used within AuthorizationProvider');
  }

  return context;
};

export {
  useAuthorizationState,
  useAuthorizationSelectedAuthorization,
  useAuthorizationTransactionDetails,
  useAuthorizationSelectAction,
  useAuthorizationLoadingState,
  useAuthorizationErrorState,
  useIsAuthorizationTransactionDetailsDrawerOpen,
  useToggleAuthorizationTransactionDetailsDrawer,
  useCheckAuthorization,
  useOnToggleAuthFlowDrawer,
  useIsAuthFlowDrawerOpen,
  useOnTransactionDetailsMutation,
  useExecuteBulkMandatesService,
  useInitiateMandateService,
  useInitiateBulkMandatesService,
  useExecuteMandateService,
  useOnCheckAuthorizationMutation,
  useOnBulkAuthorizationSelect,
  useBulkSelectedAuthorizations,
  useOnMassiveAuthorizationSelection,
  useOnSetRequestNewRate,
  useRequestNewRateFlag,
  useAuthAction,
  useOnSetAuthAction,
  useOnShouldShowRequestNewRateBtn,
};
